import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'
import { messageProvider } from '@/utils/MessageProvider'
import { ApiTaLi130 } from '~/endpoints/ApiTaLi130'
import { ApiTaLi160 } from '~/endpoints/ApiTaLi160'

export interface ContractorCourse {
  courseId: string
  name: string
}

export interface Contractor {
  localUID: string
  firstName: string
  lastName: string
  kanaName: string
  courses: ContractorCourse[]
}

/** /ta/privileges レスポンスに対応する権限情報 */
export interface PrivilegeResponse {
  /** アクセストークンにlearner権限が存在しない場合にtrue */
  hasNoLearner: boolean
  /** learner権限は存在するが、22小学の契約に該当するものがない場合にtrue */
  hasOnlyOtherLearner: boolean
}

export interface StateContractors {
  contractors: Contractor[]
  statusCode: number
  error: {
    messageId: string
    message: string
  } | null
}

export const getEmptyStateContractors = (): StateContractors => ({
  contractors: [],
  statusCode: 0,
  error: null,
})

export interface StatePrivileges {
  privilegeResponse: PrivilegeResponse
  statusCode: number
  error: {
    messageId: string
    message: string
  } | null
}

export const getEmptyStatePrivileges = (): StatePrivileges => ({
  privilegeResponse: {
    hasNoLearner: false,
    hasOnlyOtherLearner: false,
  },
  statusCode: 0,
  error: null,
})

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/Contractors',
})
export default class Contractors extends VuexModule {
  private state_: StateContractors = getEmptyStateContractors()
  private statePrivileges: StatePrivileges = getEmptyStatePrivileges()

  get result(): StateContractors {
    return this.state_
  }

  get privileges(): StatePrivileges {
    return this.statePrivileges
  }

  @Mutation
  updateContractors(value: StateContractors) {
    this.state_ = value
  }

  @Mutation
  updatePrivileges(value: StatePrivileges) {
    this.statePrivileges = value
  }

  static readonly MUTATION_NAME_UPDATE_STATE = 'updateContractors'
  static readonly MUTATION_NAME_UPDATE_PRIVILEGES = 'updatePrivileges'

  @Action
  clear() {
    this.context.commit(Contractors.MUTATION_NAME_UPDATE_STATE, getEmptyStateContractors())
    this.context.commit(Contractors.MUTATION_NAME_UPDATE_PRIVILEGES, getEmptyStatePrivileges())
  }

  @Action
  async getContractors() {
    logger.trace('in: getContractors')
    const api = new ApiTaLi130.Api()

    const result = await api.get<StateContractors>(
      {},
      (data, status) => {
        return {
          contractors: data.contractors,
          statusCode: status,
          error: null,
        }
      },
      (data, status) => {
        return {
          contractors: getEmptyStateContractors().contractors,
          statusCode: status,
          error: {
            messageId: data.error.messageId,
            message: data.error.message,
          },
        }
      }
    )

    const commitdata: StateContractors = getEmptyStateContractors()
    if (result) {
      commitdata.statusCode = result.statusCode
      commitdata.error = result.error
      if (commitdata.error === null) {
        commitdata.contractors = result.contractors
      }
    } else {
      const msgId = 'MSG-00-00023-E'
      commitdata.error = {
        messageId: msgId,
        message: messageProvider.get(msgId),
      }
    }
    this.context.commit(Contractors.MUTATION_NAME_UPDATE_STATE, commitdata)
  }

  @Action
  async getPrivileges() {
    logger.trace('in: getPrivileges')
    const api = new ApiTaLi160.Api()

    const result = await api.get<StatePrivileges>(
      {},
      (data, status) => {
        return {
          privilegeResponse: {
            hasNoLearner: data.hasNoLearner,
            hasOnlyOtherLearner: data.hasOnlyOtherLearner,
          },
          statusCode: status,
          error: null,
        }
      },
      (data, status) => {
        return {
          privilegeResponse: getEmptyStatePrivileges().privilegeResponse,
          statusCode: status,
          error: {
            messageId: data.error.messageId,
            message: data.error.message,
          },
        }
      }
    )

    const commitdata: StatePrivileges = getEmptyStatePrivileges()
    if (result) {
      commitdata.statusCode = result.statusCode
      commitdata.error = result.error
      if (commitdata.error === null) {
        commitdata.privilegeResponse = result.privilegeResponse
      }
    } else {
      const msgId = 'MSG-00-00023-E'
      commitdata.error = {
        messageId: msgId,
        message: messageProvider.get(msgId),
      }
    }
    this.context.commit(Contractors.MUTATION_NAME_UPDATE_PRIVILEGES, commitdata)
  }
}
